import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import { Button } from '../components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import { Sun, Moon, User, SignOut, Gear, MagnifyingGlass, CreditCard, EnvelopeSimple, Buildings, House, UserGear, Bookmark } from '@phosphor-icons/react';

const LOGO_URL = 'https://customer-assets.emergentagent.com/job_compliance-hub-623/artifacts/04kw0jh0_IMG_4683.jpeg';

export const Navbar = () => {
  const { user, logout, isAdmin } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const navigate = useNavigate();

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  return (
    <nav className="sticky top-0 z-50 bg-background/90 backdrop-blur-xl border-b border-border">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          {/* Logo */}
          <Link to="/" className="flex items-center space-x-2" data-testid="logo-link">
            <img src={LOGO_URL} alt="Vantage Fleet & Compliance" className="h-10 w-auto" />
            <span className="text-lg font-bold tracking-tight hidden sm:inline leading-tight">
              <span className="block text-sm">Vantage Fleet</span>
              <span className="block text-xs text-muted-foreground font-medium">Roadworthiness Guide</span>
            </span>
          </Link>

          {/* Right side */}
          <div className="flex items-center space-x-2">
            {/* Contact & Corporate links */}
            <Link to="/contact">
              <Button variant="ghost" size="sm" className="hidden sm:flex" data-testid="contact-nav-btn">
                <EnvelopeSimple weight="duotone" className="h-4 w-4 mr-2" />
                Contact
              </Button>
            </Link>
            <Link to="/corporate">
              <Button variant="ghost" size="sm" className="hidden sm:flex" data-testid="corporate-nav-btn">
                <Buildings weight="duotone" className="h-4 w-4 mr-2" />
                Corporate
              </Button>
            </Link>

            {user && user.subscription_status === 'active' && (
              <Link to="/dashboard">
                <Button variant="ghost" size="sm" className="hidden sm:flex" data-testid="search-nav-btn">
                  <MagnifyingGlass weight="bold" className="h-4 w-4 mr-2" />
                  Search
                </Button>
              </Link>
            )}

            {user && (
              <Link to="/">
                <Button variant="ghost" size="sm" className="hidden sm:flex" data-testid="home-nav-btn">
                  <House weight="duotone" className="h-4 w-4 mr-2" />
                  Home
                </Button>
              </Link>
            )}

            {/* Theme toggle */}
            <Button
              variant="ghost"
              size="icon"
              onClick={toggleTheme}
              data-testid="theme-toggle"
              className="h-9 w-9"
            >
              {theme === 'dark' ? (
                <Sun weight="duotone" className="h-5 w-5" />
              ) : (
                <Moon weight="duotone" className="h-5 w-5" />
              )}
            </Button>

            {/* User menu or auth buttons */}
            {user ? (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" size="icon" className="h-9 w-9" data-testid="user-menu-trigger">
                    <User weight="duotone" className="h-5 w-5" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56">
                  <div className="px-2 py-1.5">
                    <p className="text-sm font-medium">{user.name || user.email}</p>
                    <p className="text-xs text-muted-foreground">{user.email}</p>
                  </div>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={() => navigate('/account')} data-testid="account-menu-item">
                    <UserGear weight="duotone" className="mr-2 h-4 w-4" />
                    Account Settings
                  </DropdownMenuItem>
                  {user.subscription_status === 'active' && (
                    <DropdownMenuItem onClick={() => navigate('/bookmarks')} data-testid="bookmarks-menu-item">
                      <Bookmark weight="duotone" className="mr-2 h-4 w-4" />
                      Saved Offences
                    </DropdownMenuItem>
                  )}
                  <DropdownMenuItem onClick={() => navigate('/subscription')} data-testid="subscription-menu-item">
                    <CreditCard weight="duotone" className="mr-2 h-4 w-4" />
                    Subscription
                  </DropdownMenuItem>
                  {isAdmin && (
                    <>
                      <DropdownMenuItem onClick={() => navigate('/admin')} data-testid="admin-menu-item">
                        <Gear weight="duotone" className="mr-2 h-4 w-4" />
                        Admin Panel
                      </DropdownMenuItem>
                    </>
                  )}
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={handleLogout} data-testid="logout-btn">
                    <SignOut weight="duotone" className="mr-2 h-4 w-4" />
                    Logout
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            ) : (
              <div className="flex items-center space-x-2">
                <Link to="/login">
                  <Button variant="ghost" size="sm" data-testid="login-nav-btn">
                    Login
                  </Button>
                </Link>
                <Link to="/register">
                  <Button size="sm" data-testid="register-nav-btn">
                    Get Started
                  </Button>
                </Link>
              </div>
            )}
          </div>
        </div>
      </div>
    </nav>
  );
};
